import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
    ActivityIndicator,
    FlatList,
    Keyboard,
    KeyboardAvoidingView,
    Platform,
    RefreshControl,
    StyleSheet,
    TouchableOpacity,
    useWindowDimensions,
    View,
} from 'react-native';
import { ThemeConsumer } from '@rneui/themed';
import { NavigationProp, useIsFocused, useNavigation } from '@react-navigation/native';
import Popover from 'react-native-popover-view';
import { shallowEqual } from 'react-redux';

import CommonHeader from '../../../shared/commonComponents/commonHeader';
import { ListToolbar } from '../../../shared/commonComponents/listToolbar';
import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox';
import {
    ZenBadge,
    ZenEmptyComponent,
    ZenFilterModal,
    ZenIcon,
    ZenLoader,
    ZenText,
    ZenToast,
} from 'zen-core-elements';
import { filterItemsProps } from 'zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal';

import { appColors, appFonts, fontSize } from '../../../themes/appTheme';
import { config } from '../../../shared/environment';
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
    clearOptionCache,
    getAllPosOptionList,
    getOneDeletePosOptionList,
} from '../../../redux/slices/optionSlice';
import { FilterType, NavigationType, OptionListType } from './optionModule';
import { SortOption } from '../../../shared/commonComponents/commonSortModal';

const LIMIT = 20;

const OptionList: FC = () => {
    const { width } = useWindowDimensions();
    const isLandscape = width > 768;
    const dispatch = useAppDispatch();
    const navigation = useNavigation<NavigationProp<NavigationType>>();
    const isFocused = useIsFocused();

    // Permission check
    const { isWrite = true } =
        useAppSelector(getScreenPermission('OptionStack'), shallowEqual) || {};

    // Component states
    const [options, setOptions] = useState<OptionListType[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [load, setLoad] = useState<boolean>(true);
    const [selectedSortOption, setSelectedSortOption] = useState({ value: "", index: -1 })
    const [refresh, setRefresh] = useState<boolean>(false);
    const [isFooterLoading, setIsFooterLoading] = useState<boolean>(false);

    //Variable to maintain sort values to display
    const sortOptionsData: SortOption[] = [
        { label: 'Option name A-Z', column: 'name', order: 'ASC', id: 1 },
        { label: 'Option name Z-A', column: 'name', order: 'DESC', id: 2 },
        { label: 'Recently created first', column: 'createdAt', order: 'ASC', id: 3 },
        { label: 'Recently created last', column: 'createdAt', order: 'DESC', id: 4 },
        { label: 'Recently updated first', column: 'modifiedAt', order: 'ASC', id: 5 },
        { label: 'Recently updated last', column: 'modifiedAt', order: 'DESC', id: 6 }
    ]

    // Search and Filter states
    const [searchText, setSearchText] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [filterOpen, setFilterOpen] = useState<boolean>(false);
    const [filterCount, setFilterCount] = useState<number>(0);
    const [filterPayload, setFilterPayload] = useState<FilterType>(null);
    const [filterData, setFilterData] = useState<filterItemsProps[]>([
        {
            id: '1',
            filterName: 'medium',
            filterType: 'radio',
            initialSelected: '',
            filterValue: [
                { value: 'POS', label: 'POS' },
                { value: 'WEB', label: 'WEB' },
            ],
        },
    ]);

    // Delete dialog states
    const [deletePopoverVisible, setDeletePopoverVisible] = useState<boolean>(false);
    const [selectedId, setSelectedId] = useState<number>(0);

    // Track scroll momentum for pagination
    const onMomentumScrollBegin = useRef(false);
    const isFirstRender = useRef(true);
    const isRefreshing = useRef(false);

    // Search debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchText);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchText]);

    // Main fetch function with Redux caching support
    const fetchOptionList = useCallback(
        async (
            targetOffset: number,
            bypassCache: boolean = false,
            overrideSearch?: string,
            overrideSort?: string,
            overrideFilter?: string
        ) => {
            const currentSearch = overrideSearch !== undefined ? overrideSearch : debouncedSearch;
            const currentSort =
                overrideSort !== undefined
                    ? overrideSort
                    : (selectedSortOption?.value ||
                        JSON.stringify({ field: 'name', order: 'ASC' }));
            const currentFilter =
                overrideFilter !== undefined
                    ? overrideFilter
                    : filterPayload?.values?.medium
                        ? JSON.stringify({ "medium": filterPayload.values.medium })
                        : '';

            const payload = {
                offset: targetOffset,
                limit: LIMIT,
                searchText: currentSearch,
                sortOrder: currentSort,
                filterData: currentFilter,
            };

            try {
                const response = await dispatch(
                    getAllPosOptionList({
                        storeId: config?.storeId,
                        data: payload,
                        refresh: bypassCache,
                    })
                ).unwrap();

                if (response) {
                    setTotal(response?.options?.count ?? 0);
                    const rows = response?.options?.rows || [];
                    if (targetOffset > 0) {
                        setOptions((prev) => [...prev, ...rows]);
                    } else {
                        setOptions(rows);
                    }
                }
            } catch (err: any) {
                console.log('Error in fetchOptionList:', err);
            } finally {
                setLoad(false);
                setRefresh(false);
                setIsFooterLoading(false);
            }
        },
        [debouncedSearch, filterPayload, selectedSortOption, dispatch, config?.storeId]
    );

    // Initial fetch and on focus refresh
    useEffect(() => {
        if (isFocused) {
            fetchOptionList(0);
        }
    }, [isFocused]);

    // Fetch on search, filter, or sort option change
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (isRefreshing.current) {
            return;
        }
        setLoad(true);
        fetchOptionList(0, true);
    }, [debouncedSearch, filterPayload, selectedSortOption]);

    // Pull-to-refresh: resets search, sort, and filter to initial state
    const handleRefresh = () => {
        setRefresh(true);
        isRefreshing.current = true;
        dispatch(clearOptionCache());
        setSearchText('');
        setDebouncedSearch('');
        setSelectedSortOption({ value: '', index: -1 });
        setFilterCount(0);
        setFilterPayload(null);
        setFilterData([
            {
                id: '1',
                filterName: 'medium',
                filterType: 'radio',
                initialSelected: '',
                filterValue: [
                    { value: 'POS', label: 'POS' },
                    { value: 'WEB', label: 'WEB' },
                ],
            },
        ]);
        fetchOptionList(
            0,
            true,
            '',
            JSON.stringify({ field: 'name', order: 'ASC' }),
            ''
        ).finally(() => {
            isRefreshing.current = false;
        });
    };

    // Infinite scroll pagination
    const handleEndReached = () => {
        if (onMomentumScrollBegin.current && !isFooterLoading && options.length < total) {
            setIsFooterLoading(true);
            onMomentumScrollBegin.current = false;
            fetchOptionList(options.length);
        }
    };

    // Delete confirmation handler
    const handleDeleteConfirmation = async (message: { id: number }) => {
        if (message?.id === 2 && selectedId) {
            setDeletePopoverVisible(false);
            try {
                const res = await dispatch(
                    getOneDeletePosOptionList({
                        storeId: config.storeId,
                        deleteId: selectedId,
                    })
                ).unwrap();
                if (res) {
                    ZenToast({
                        type: 'success',
                        message: res?.message || 'Option deleted successfully',
                    });
                    setLoad(true);
                    fetchOptionList(0, true);
                }
            } catch (err: any) {
                console.log('Error deleting option:', err);
                ZenToast({
                    type: 'error',
                    message: err?.message || 'Failed to delete option',
                });
            } finally {
                setSelectedId(0);
            }
        } else {
            setDeletePopoverVisible(false);
            setSelectedId(0);
        }
    };

    // Render individual option card
    const renderOptionCard = useCallback(
        ({ item }: { item: OptionListType }) => {
            const isPosMedium = item?.medium === 'POS';
            const isWebMedium = item?.medium === 'WEB';

            const badgeBg = isPosMedium
                ? '#E0F2FE'
                : isWebMedium
                    ? '#FEF3C7'
                    : '#F1F5F9';
            const badgeTextColor = isPosMedium
                ? appColors.primary
                : isWebMedium
                    ? '#D97706'
                    : '#475569';

            return (
                <ThemeConsumer>
                    {({ theme }) => (
                        <View style={[styles.card, isLandscape && styles.gridCard]}>
                            {/* Card Left: Icon + Name & Medium Badge */}
                            <View style={styles.cardInfo}>
                                <View style={styles.iconCircle}>
                                    <ZenIcon
                                        type="material-community"
                                        name="format-list-bulleted"
                                        size={20}
                                        color={theme?.primary ?? appColors.primary}
                                    />
                                </View>

                                <View style={styles.detailsCol}>
                                    <ZenText
                                        content={item?.name || 'Unnamed Option'}
                                        bold
                                        numberOfLines={1}
                                        size={theme?.mediumFontSize ?? fontSize.mediumFontSize}
                                        color={theme?.dark ?? appColors.dark}
                                        style={styles.optionName}
                                    />

                                    {item?.medium ? (
                                        <View style={styles.badgeRow}>
                                            <ZenBadge
                                                badgeName={item.medium}
                                                badgeColor={badgeBg}
                                                badgeTextColor={badgeTextColor}
                                                badgeContainerStyle={styles.mediumBadge}
                                                badgeTextStyle={styles.badgeText}
                                            />
                                        </View>
                                    ) : null}
                                </View>
                            </View>

                            {/* Card Right: Action Buttons */}
                            {isWrite && (
                                <View style={styles.actionsRow}>
                                    {/* Edit Option Button */}
                                    <TouchableOpacity
                                        activeOpacity={0.7}
                                        style={styles.editButton}
                                        onPress={() => {
                                            navigation.navigate('AddEditOption', {
                                                isEdit: true,
                                                selectedId: item.id,
                                            });
                                        }}
                                        accessibilityLabel={`Edit option ${item.name}`}
                                    >
                                        <ZenIcon
                                            name="pencil-outline"
                                            type="material-community"
                                            size={18}
                                            color={theme?.primary ?? appColors.primary}
                                        />
                                    </TouchableOpacity>

                                    {/* Delete Option Button */}
                                    <TouchableOpacity
                                        activeOpacity={0.7}
                                        style={styles.deleteButton}
                                        onPress={() => {
                                            setSelectedId(item.id);
                                            setDeletePopoverVisible(true);
                                        }}
                                        accessibilityLabel={`Delete option ${item.name}`}
                                    >
                                        <ZenIcon
                                            name="delete-outline"
                                            type="material-community"
                                            size={18}
                                            color={appColors.red}
                                        />
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                    )}
                </ThemeConsumer>
            );
        },
        [isLandscape, isWrite, navigation]
    );

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView
                    onStartShouldSetResponder={() => {
                        Keyboard.dismiss();
                        return false;
                    }}
                    behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                    style={styles.screenContainer}
                >
                    {/* Header */}
                    <CommonHeader headerName="Option List" />

                    {/* Integrated ListToolbar */}
                    <ListToolbar
                        search={{
                            value: searchText,
                            onChange: (val) =>
                                setSearchText(
                                    typeof val === 'function' ? val(searchText) : val
                                ),
                            onCancel: () => setSearchText(''),
                            placeholder: 'Search option by name...',
                        }}
                        filter={{
                            count: filterCount,
                            onPress: () => setFilterOpen(true),
                        }}
                        sort={{
                            options: sortOptionsData,
                            selectedId: selectedSortOption?.index,
                            onSelect: (item) => {
                                setSelectedSortOption({ value: JSON.stringify({ "field": item?.column, "order": item?.order }), index: item?.id! })
                            },
                            title: "Sort by"

                        }}
                        buttons={
                            isWrite
                                ? [
                                    {
                                        title: 'Sort',
                                        variant: 'outline',
                                        onPress: () =>
                                            navigation.navigate('SortOptionList', {
                                                isList: true,
                                                selectedId: 0,
                                                sortOptionValue: options,
                                            }),
                                    },
                                    {
                                        title: 'Add Option',
                                        variant: 'primary',
                                        onPress: () =>
                                            navigation.navigate('AddEditOption', {
                                                isEdit: false,
                                                selectedId: 0,
                                            }),
                                    },
                                ]
                                : []
                        }
                    />

                    {/* Content List */}
                    {load ? (
                        <View style={styles.loaderContainer}>
                            <ZenLoader
                                lottieType="Local"
                                lottie={require('../../../assets/infinityLoader.json')}
                            />
                        </View>
                    ) : (
                        <FlatList
                            key={isLandscape ? 'grid-2-col' : 'list-1-col'}
                            data={options}
                            renderItem={renderOptionCard}
                            keyExtractor={(item, index) =>
                                item?.id ? `${item.id}-${index}` : index.toString()
                            }
                            numColumns={isLandscape ? 2 : 1}
                            columnWrapperStyle={
                                isLandscape ? styles.columnWrapper : undefined
                            }
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={[
                                styles.listContent,
                                !options.length && styles.emptyContent,
                            ]}
                            onMomentumScrollBegin={() => {
                                onMomentumScrollBegin.current = true;
                            }}
                            onEndReached={handleEndReached}
                            onEndReachedThreshold={0.1}
                            initialNumToRender={20}
                            refreshControl={
                                <RefreshControl
                                    refreshing={refresh}
                                    onRefresh={handleRefresh}
                                    colors={[theme?.primary ?? appColors.primary]}
                                    tintColor={theme?.primary ?? appColors.primary}
                                />
                            }
                            ListEmptyComponent={
                                <ZenEmptyComponent
                                    text="No options found"
                                    viewStyle={styles.emptyComponentView}
                                />
                            }
                            ListFooterComponent={
                                isFooterLoading ? (
                                    <View style={styles.footerLoader}>
                                        <ActivityIndicator
                                            size="small"
                                            color={theme?.primary ?? appColors.primary}
                                        />
                                    </View>
                                ) : null
                            }
                        />
                    )}

                    {/* Mobile Floating Action Button */}
                    {!isLandscape && isWrite && (
                        <TouchableOpacity
                            activeOpacity={0.8}
                            style={[
                                styles.fab,
                                {
                                    backgroundColor:
                                        theme?.primary ?? appColors.primary,
                                },
                            ]}
                            onPress={() =>
                                navigation.navigate('AddEditOption', {
                                    isEdit: false,
                                    selectedId: 0,
                                })
                            }
                            accessibilityLabel="Add Option"
                        >
                            <ZenIcon
                                type="material-community"
                                name="plus"
                                size={28}
                                color={appColors.light}
                            />
                        </TouchableOpacity>
                    )}

                    {/* Medium Filter Modal */}
                    <ZenFilterModal
                        title="Option Filter"
                        visible={filterOpen}
                        setVisible={setFilterOpen}
                        filterItems={filterData}
                        filterCount={filterCount}
                        setFilterCount={setFilterCount}
                        setFilterItems={setFilterData}
                        onConfirm={(val: any) => {
                            if (val?.values?.medium) {
                                setFilterPayload({ values: { medium: val.values.medium } });
                            } else {
                                setFilterPayload(null);
                            }
                        }}
                    />

                    {/* Delete Confirmation Popover */}
                    <Popover
                        isVisible={deletePopoverVisible}
                        onRequestClose={() => {
                            setDeletePopoverVisible(false);
                            setSelectedId(0);
                        }}
                        popoverStyle={styles.popoverDialog}
                    >
                        <CommonDialogBox
                            type="DELETE"
                            replaceValue="option"
                            confirmation={handleDeleteConfirmation}
                        />
                    </Popover>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    );
};

export default OptionList;

const styles = StyleSheet.create({
    screenContainer: {
        flex: 1,
        backgroundColor: '#F8FAFC',
    },
    loaderContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    listContent: {
        paddingHorizontal: 16,
        paddingTop: 10,
        paddingBottom: 80,
    },
    columnWrapper: {
        columnGap: 14,
    },
    emptyContent: {
        flexGrow: 1,
        justifyContent: 'center',
    },
    emptyComponentView: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 40,
    },
    footerLoader: {
        paddingVertical: 16,
        alignItems: 'center',
    },
    card: {
        backgroundColor: appColors.light,
        borderRadius: 14,
        padding: 14,
        marginBottom: 12,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.04,
        shadowRadius: 6,
        elevation: 2,
    },
    gridCard: {
        flex: 1,
    },
    cardInfo: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 12,
    },
    iconCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: '#F0F7FA',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: 'rgba(0, 83, 116, 0.08)',
    },
    detailsCol: {
        flex: 1,
        rowGap: 4,
    },
    optionName: {
        fontFamily: appFonts.bold,
        textTransform: 'capitalize',
    },
    badgeRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    mediumBadge: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 6,
    },
    badgeText: {
        fontFamily: appFonts.medium,
        fontSize: fontSize.badgeFontSize,
    },
    actionsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 8,
        marginLeft: 10,
    },
    editButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: '#F1F5F9',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    deleteButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: '#FEEAEA',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#FECDD3',
    },
    fab: {
        position: 'absolute',
        bottom: 24,
        right: 24,
        width: 56,
        height: 56,
        borderRadius: 28,
        justifyContent: 'center',
        alignItems: 'center',
        elevation: 6,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 8,
    },
    popoverDialog: {
        borderRadius: 14,
        overflow: 'hidden',
    },
});